import React, { useState } from 'react';
import {
  downloadInvoice,
  fetchInvoiceData,
  downloadMonthlyReport,
  downloadCustomReport,
  formatCurrency,
  formatDate,
} from '../utils/reportUtils';
import { Card, Btn, FormGroup, Badge, SectionHeader } from './UI';

/* ── helpers ─────────────────────────────────────────────── */
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const YEAR_RANGE = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

/* ── Invoice Preview ─────────────────────────────────────── */
function InvoicePreview({ data }) {
  if (!data) return null;

  // Support both shapes: { booking, customer, company } or { invoice, customer, biller }
  const booking  = data.booking  || data.invoice  || {};
  const customer = data.customer || {};
  const company  = data.company  || data.biller   || {};
  const items    = data.items    || [];
  const totals   = data.total    || {};

  const amount  = parseFloat(booking.amount || totals.subtotal || 0);
  const total   = parseFloat(totals.total || amount);

  return (
    <div style={{
      background:'var(--bg-elevated)', border:'1px solid var(--border-default)',
      borderRadius:'var(--radius-lg)', overflow:'hidden', marginTop:16,
    }}>
      {/* Header */}
      <div style={{
        background:'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.04))',
        borderBottom:'1px solid var(--brand-glow)', padding:'20px 24px',
        display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:16,
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <span style={{ fontSize:20 }}>🔥</span>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:'var(--text-primary)' }}>{company.name || 'Venad Communications'}</div>
              <div style={{ fontSize:11, color:'var(--text-muted)' }}>IndianOil Gas Distributor</div>
            </div>
          </div>
          <div style={{ fontSize:11.5, color:'var(--text-muted)', lineHeight:1.7 }}>
            {company.address || 'Thiruvananthapuram, Kerala'}<br />
            {company.gstin && <>GSTIN: {company.gstin} · </>}
            {company.phone && <>Ph: {company.phone}</>}
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>Invoice</div>
          <div style={{ fontSize:22, fontWeight:800, color:'var(--brand)', fontFamily:'var(--font-mono)', marginTop:2 }}>
            #{booking.bookingNo || booking.invoiceNumber || booking.id || 'N/A'}
          </div>
          <Badge type={booking.status === 'Delivered' ? 'success' : 'warning'} dot>
            {booking.status || 'Pending'}
          </Badge>
        </div>
      </div>

      {/* Bill-to + Details */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', borderBottom:'1px solid var(--border-subtle)' }}>
        <div style={{ padding:'16px 24px', borderRight:'1px solid var(--border-subtle)' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Bill To</div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>{customer.name || '—'}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.8 }}>
            {customer.connectionNo && <div>Conn: <span style={{ fontFamily:'var(--font-mono)', color:'var(--accent-blue)' }}>{customer.connectionNo}</span></div>}
            {customer.phone && <div>{customer.phone}</div>}
            {customer.address && <div>{customer.address}</div>}
          </div>
        </div>
        <div style={{ padding:'16px 24px' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Booking Info</div>
          {[
            ['Booking Date',   formatDate(booking.bookingDate)],
            ['Delivery Date',  booking.deliveryDate ? formatDate(booking.deliveryDate) : 'Pending'],
            ['Cylinder Type',  booking.cylinderType],
          ].filter(([,v])=>v).map(([l,v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:12 }}>
              <span style={{ color:'var(--text-muted)' }}>{l}</span>
              <span style={{ color:'var(--text-secondary)', fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Line items */}
      <div style={{ padding:'16px 24px' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>
              {['Description','HSN','Qty','Rate','Amount'].map(h => (
                <th key={h} style={{
                  textAlign: h==='Amount'||h==='Rate' ? 'right' : 'left',
                  padding:'7px 10px', fontSize:10.5, fontWeight:700,
                  color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em',
                  borderBottom:'1px solid var(--border-subtle)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? items.map((item, i) => (
              <tr key={i}>
                <td style={{ padding:'10px', color:'var(--text-primary)', fontWeight:500 }}>{item.description || item.name || booking.cylinderType}</td>
                <td style={{ padding:'10px', fontFamily:'var(--font-mono)', color:'var(--text-muted)', fontSize:12 }}>{item.hsn || '27111900'}</td>
                <td style={{ padding:'10px', color:'var(--text-secondary)' }}>{item.quantity || 1}</td>
                <td style={{ padding:'10px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--text-primary)', fontWeight:600 }}>{formatCurrency(item.rate || item.price || amount)}</td>
                <td style={{ padding:'10px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--text-primary)', fontWeight:600 }}>{formatCurrency(item.amount || item.total || amount)}</td>
              </tr>
            )) : (
              <tr>
                <td style={{ padding:'10px', color:'var(--text-primary)', fontWeight:500 }}>{booking.cylinderType || 'LPG Cylinder'}</td>
                <td style={{ padding:'10px', fontFamily:'var(--font-mono)', color:'var(--text-muted)', fontSize:12 }}>27111900</td>
                <td style={{ padding:'10px', color:'var(--text-secondary)' }}>1</td>
                <td style={{ padding:'10px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--text-primary)', fontWeight:600 }}>{formatCurrency(amount)}</td>
                <td style={{ padding:'10px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--text-primary)', fontWeight:600 }}>{formatCurrency(amount)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ borderTop:'1px solid var(--border-subtle)', padding:'14px 24px', display:'flex', justifyContent:'flex-end' }}>
        <div style={{ minWidth:240 }}>
          {[
            ['Subtotal', formatCurrency(amount)],
          ].map(([l, v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--border-subtle)' }}>
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>{l}</span>
              <span style={{ fontSize:13, fontFamily:'var(--font-mono)', color:'var(--text-secondary)' }}>{v}</span>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', marginTop:4 }}>
            <span style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>Total</span>
            <span style={{ fontSize:18, fontWeight:800, fontFamily:'var(--font-mono)', color:'var(--accent-green)' }}>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      <div style={{ background:'var(--bg-surface)', borderTop:'1px solid var(--border-subtle)', padding:'10px 24px', fontSize:11, color:'var(--text-muted)', textAlign:'center' }}>
        Thank you for your business · Computer-generated invoice — no signature required.
      </div>
    </div>
  );
}

/* ── Section wrapper card ─────────────────────────────────── */
function ReportCard({ icon, title, desc, children }) {
  return (
    <div style={{
      background:'var(--bg-surface)', border:'1px solid var(--border-subtle)',
      borderRadius:'var(--radius-lg)', padding:'20px 22px', display:'flex', flexDirection:'column', gap:14,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ fontSize:20, width:40, height:40, background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)' }}>{title}</div>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{desc}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

/* ── Error / Success message ─────────────────────────────── */
function Msg({ type, text }) {
  if (!text) return null;
  const isErr = type === 'error';
  return (
    <div style={{
      padding:'9px 14px', borderRadius:'var(--radius-md)', fontSize:12.5, fontWeight:500,
      background: isErr ? 'var(--accent-red-dim)' : 'var(--accent-green-dim)',
      border: `1px solid ${isErr ? 'rgba(239,68,68,.25)' : 'rgba(34,197,94,.25)'}`,
      color: isErr ? 'var(--accent-red)' : 'var(--accent-green)',
      display:'flex', alignItems:'center', gap:8,
    }}>
      {isErr ? '⚠' : '✓'} {text}
    </div>
  );
}

/* ── Main ReportGenerator ─────────────────────────────────── */
export function ReportGenerator({ bookings = [], customers = [] }) {
  // Invoice state
  const [bookingId,   setBookingId]   = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [invLoading,  setInvLoading]  = useState(false);
  const [invMsg,      setInvMsg]      = useState({ type:'', text:'' });

  // Monthly state
  const [month,     setMonth]     = useState(new Date().getMonth() + 1);
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [monLoading,setMonLoading]= useState(false);
  const [monMsg,    setMonMsg]    = useState({ type:'', text:'' });

  // Custom range state
  const [startDate,  setStartDate]  = useState('');
  const [endDate,    setEndDate]    = useState('');
  const [custLoading,setCustLoading]= useState(false);
  const [custMsg,    setCustMsg]    = useState({ type:'', text:'' });

  /* ── Invoice handlers ─── */
  const handlePreview = async () => {
    setInvMsg({ type:'', text:'' }); setInvoiceData(null); setInvLoading(true);
    try {
      const data = await fetchInvoiceData(bookingId);
      setInvoiceData(data);
    } catch (e) {
      setInvMsg({ type:'error', text: e.message });
    } finally { setInvLoading(false); }
  };

  const handleDownloadInvoice = async () => {
    setInvMsg({ type:'', text:'' }); setInvLoading(true);
    try {
      await downloadInvoice(bookingId);
      setInvMsg({ type:'success', text: 'Invoice downloaded successfully!' });
    } catch (e) {
      setInvMsg({ type:'error', text: e.message });
    } finally { setInvLoading(false); }
  };

  /* ── Monthly handler ─── */
  const handleMonthly = async () => {
    setMonMsg({ type:'', text:'' }); setMonLoading(true);
    try {
      await downloadMonthlyReport(month, year);
      setMonMsg({ type:'success', text: `${MONTHS[month-1]} ${year} report downloaded!` });
    } catch (e) {
      setMonMsg({ type:'error', text: e.message });
    } finally { setMonLoading(false); }
  };

  /* ── Custom range handler ─── */
  const handleCustom = async () => {
    setCustMsg({ type:'', text:'' }); setCustLoading(true);
    try {
      await downloadCustomReport(startDate, endDate);
      setCustMsg({ type:'success', text: 'Custom report downloaded!' });
    } catch (e) {
      setCustMsg({ type:'error', text: e.message });
    } finally { setCustLoading(false); }
  };

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h3 style={{ fontSize:16, fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.02em' }}>Generate &amp; Export</h3>
        <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:4 }}>Download invoices and reports as PDF from the server</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:16 }}>

        {/* ── Invoice Card ── */}
        <ReportCard icon="🧾" title="Booking Invoice" desc="Preview or download a PDF invoice for any booking">
          <FormGroup label="Booking ID">
            <input
              value={bookingId}
              onChange={e => { setBookingId(e.target.value); setInvMsg({ type:'', text:'' }); setInvoiceData(null); }}
              placeholder="Enter booking ID (e.g. 16)"
              className="form-input"
              type="number"
              onKeyDown={e => e.key === 'Enter' && handlePreview()}
            />
          </FormGroup>

          <Msg type={invMsg.type} text={invMsg.text} />

          <div style={{ display:'flex', gap:8 }}>
            <Btn
              variant="secondary"
              onClick={handlePreview}
              disabled={invLoading || !bookingId}
              icon="👁"
            >
              {invLoading ? 'Loading…' : 'Preview'}
            </Btn>
            <Btn
              variant="primary"
              onClick={handleDownloadInvoice}
              disabled={invLoading || !bookingId}
              icon="⬇"
            >
              Download PDF
            </Btn>
          </div>

          <InvoicePreview data={invoiceData} />
        </ReportCard>

        {/* ── Monthly Report Card ── */}
        <ReportCard icon="📅" title="Monthly Report" desc="Download a full summary of all bookings for a given month (admin only)">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <FormGroup label="Month">
              <select value={month} onChange={e => setMonth(Number(e.target.value))} className="form-select">
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </FormGroup>
            <FormGroup label="Year">
              <select value={year} onChange={e => setYear(Number(e.target.value))} className="form-select">
                {YEAR_RANGE.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </FormGroup>
          </div>

          <Msg type={monMsg.type} text={monMsg.text} />

          <Btn
            variant="primary"
            onClick={handleMonthly}
            disabled={monLoading}
            icon="⬇"
            fullWidth
          >
            {monLoading ? 'Generating…' : `Download ${MONTHS[month-1]} ${year} Report`}
          </Btn>
        </ReportCard>

        {/* ── Custom Date Range Card ── */}
        <ReportCard icon="📆" title="Custom Date Range" desc="Export all bookings between any two dates (admin only)">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <FormGroup label="Start Date">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="form-input"
              />
            </FormGroup>
            <FormGroup label="End Date">
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={e => setEndDate(e.target.value)}
                className="form-input"
              />
            </FormGroup>
          </div>

          {startDate && endDate && (
            <div style={{ fontSize:12, color:'var(--text-muted)' }}>
              Range: <strong style={{ color:'var(--text-secondary)' }}>{formatDate(startDate)}</strong>
              {' → '}
              <strong style={{ color:'var(--text-secondary)' }}>{formatDate(endDate)}</strong>
            </div>
          )}

          <Msg type={custMsg.type} text={custMsg.text} />

          <Btn
            variant="primary"
            onClick={handleCustom}
            disabled={custLoading || !startDate || !endDate}
            icon="⬇"
            fullWidth
          >
            {custLoading ? 'Generating…' : 'Download Custom Report'}
          </Btn>
        </ReportCard>

      </div>

      {/* Quick reference */}
      {bookings.length > 0 && (
        <div style={{ marginTop:22, padding:'14px 20px', background:'var(--bg-surface)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-lg)' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>Quick Reference</div>
          <div style={{ display:'flex', gap:24, flexWrap:'wrap', fontSize:12 }}>
            <span><span style={{ color:'var(--text-muted)' }}>Total bookings: </span><strong style={{ fontFamily:'var(--font-mono)', color:'var(--text-primary)' }}>{bookings.length}</strong></span>
            <span><span style={{ color:'var(--text-muted)' }}>ID range: </span><strong style={{ fontFamily:'var(--font-mono)', color:'var(--text-primary)' }}>{bookings[bookings.length-1]?.id} – {bookings[0]?.id}</strong></span>
            <span><span style={{ color:'var(--text-muted)' }}>Customers: </span><strong style={{ fontFamily:'var(--font-mono)', color:'var(--text-primary)' }}>{customers.length}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportGenerator;
